import { cloneDeep, get, isString, keys, pick, set, unset } from 'lodash';
import {
  IThemeRes,
  IThemes,
  ProjectConfigInterface,
  ScaleTypesEnum,
} from 'src/interfaces';

export const generateThemeOptions = (themes: IThemeRes[]) => {
  return themes.map((theme) => ({
    label: theme.name,
    value: theme.name,
  }));
};

export const generateDropDownOptions = ({
  pxifyLabel = true,
  pxifyValue = true,
  start = 1,
}: {
  pxifyLabel?: boolean;
  pxifyValue?: boolean;
  start?: number;
} = {}) => {
  const options = [];
  for (let i = start; i <= 150; i++) {
    const value = `${i}px`;
    options.push({
      label: pxifyLabel ? value : String(i),
      value: pxifyValue ? value : i,
    });
  }

  return options;
};

export const formatProjectConfig = (
  projectConfig = {} as Partial<ProjectConfigInterface>,
  themes = [] as IThemeRes[],
) => {
  const mappedThemes = mapThemes(themes);

  for (const themeKey of keys(mappedThemes)) {
    const border = get(
      mappedThemes,
      `${themeKey}.subproducts.label.border`,
      '',
    );
    const subheaderBorder = get(
      mappedThemes,
      `${themeKey}.subheader.border`,
      getDefaultBorder(themeKey),
    );
    const subheaderBorderRadius = get(
      mappedThemes,
      `${themeKey}.subheader.borderRadius`,
    );
    const itemBadgeSize = get(
      mappedThemes,
      `${themeKey}.badges.itemBadge.size`,
    );

    if (!itemBadgeSize) {
      set(
        mappedThemes,
        `${themeKey}.badges.itemBadge.size`,
        getDefaultBadgeSize(themeKey),
      );
    }

    if (!subheaderBorderRadius) {
      set(mappedThemes, `${themeKey}.subheader.borderRadius`, '20px');
    }

    if (subheaderBorder && isString(subheaderBorder)) {
      set(
        mappedThemes,
        `${themeKey}.subheader.border`,
        parseBorder(subheaderBorder),
      );
    }

    if (border && isString(border)) {
      set(
        mappedThemes,
        `${themeKey}.subproducts.label.border`,
        parseBorder(border),
      );
    }
  }

  return {
    ...pick(
      projectConfig,
      ['activeTheme', 'autoscale'],
      ['indoorDefaultTheme', 'autoscale'],
      ['outdoorDefaultTheme', 'autoscale'],
      ['mcCafeDefaultTheme', 'autoscale'],
      ['digitalMerchandiserDefaultTheme', 'autoscale'],
    ),
    themes: mappedThemes,
  };
};

export const mapThemes = (themes = [] as IThemeRes[]) => {
  const mappedThemes = {} as IThemes;

  themes.forEach((theme) => {
    if (theme) {
      mappedThemes[theme.name] = theme.config;
    }
  });

  return mappedThemes;
};

export const sanitizeProjectConfig = (
  projectConfig = {} as Partial<ProjectConfigInterface>,
): Partial<ProjectConfigInterface> => {
  const themes = cloneDeep(get(projectConfig, 'themes', {}));

  const themesKeys = keys(themes);

  for (const key of themesKeys) {
    const subProductsBorder = get(
      themes,
      `${key}.subproducts.label.border`,
      {},
    ) as { width: string; style: string; color: string };

    const subheaderBorder = get(themes, `${key}.subheader.border`, {}) as {
      width: string;
      style: string;
      color: string;
    };

    if (
      subheaderBorder.width &&
      subheaderBorder.style &&
      subheaderBorder.color
    ) {
      set(themes, `${key}.subheader.border`, formatBorder(subheaderBorder));
    } else {
      unset(themes, `${key}.subheader.border`);
    }

    if (
      subProductsBorder.width &&
      subProductsBorder.style &&
      subProductsBorder.color
    ) {
      set(
        themes,
        `${key}.subproducts.label.border`,
        formatBorder(subProductsBorder),
      );
    } else {
      unset(themes, `${key}.subproducts.label.border`);
    }
  }

  return { ...projectConfig, themes };
};

export const parseBorder = (border: string) => {
  if (isString(border)) {
    const [width, style, color] = border.split(' ');

    return { width, style, color };
  }

  return border;
};

export const formatBorder = (border: {
  width: string;
  style: string;
  color: string;
}) => {
  const { width, style, color } = border;

  return `${width ?? '1px'} ${style ?? 'solid'} ${color ?? '#000000'}`;
};

export const getDefaultBadgeSize = (themeKey: string): string => {
  switch (themeKey) {
    case ScaleTypesEnum.FC:
    case ScaleTypesEnum.FC_DARK_MODE:
      return '26px';
    case ScaleTypesEnum.DT:
    case ScaleTypesEnum.DT_DARK_MODE:
    case ScaleTypesEnum.DTLISTING:
    case ScaleTypesEnum.DTLISTING_DARK_MODE:
      return '25px';
    default:
      return '';
  }
};

export const getDefaultBorder = (themeKey: string): string => {
  switch (themeKey) {
    case ScaleTypesEnum.DT:
    case ScaleTypesEnum.FC:
    case ScaleTypesEnum.DTLISTING:
      return '2px solid #6D6E71';
    case ScaleTypesEnum.DT_DARK_MODE:
    case ScaleTypesEnum.DTLISTING_DARK_MODE:
    case ScaleTypesEnum.FC_DARK_MODE:
      return '2px solid #F7F7F7';
    default:
      return '';
  }
};
